import React, { useMemo } from 'react';

import { showModal } from 'modal.react.js';
import { isEmpty, omit } from 'ramda';
import { Field, Form, useForm, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';

import FormSection from 'components/Layouts/Form/FormSection';
import LayoutWithFooter from 'components/Layouts/shared/LayoutWithFooter';
import Backdrop from 'components/shared/Backdrop';
import ErrorScreen from 'components/shared/ErrorScreen';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import FormErrorText from 'components/shared/FormErrorText';
import { GeneralPopoverPagePreview } from 'components/shared/GeneralPopoverPagePreview';
import area from 'components/shared/GlobalModalArea/area';
import Loader from 'components/shared/Loader';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import {
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_AREA,
  PAGE_TYPE_MISC,
  PAGE_TYPE_PRE_TRAVEL_INFO,
  PAGE_TYPE_PROPERTY,
} from 'constants/index';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { accountIdSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';
import { isSample } from 'utils/itineraries/functions';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import { LOOKBOOK_STEPS } from 'utils/itineraries/lookbook/map';
import { isDefined } from 'utils/ramda';

import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import ItinerariesItemNextButton from '../../ItinerariesItemBuilder/ItinerariesItemNextButton';
import { prepareInitialValues } from '../../Itinerary/GeneralInfo/functions';
import { GeneralInfoTooltip } from '../../Itinerary/shared';
import SelectPage from '../../Itinerary/shared/SelectPage';
import { CustomAppsBlock } from '../../shared/Forms/CustomApps';
import WhiteLabeling from '../../shared/Forms/WhiteLabeling';
import { useTryToRemindCheckPrimaryImageForGeneralTab } from '../../shared/functions';
import { LookbookContentMissingWarningModal } from '../../shared/Modals/LookbookContentMissingWarningModal';
import ItinerarySearchFilterButton from '../../shared/SearchFilter';
import { useGeneralInfo } from './functions';
import styles from './index.module.css';
import PageMultiselect from './PageMultiselect';
import { RoomsWrapper } from './RoomsWrapper';

const inclusionsPageTypesToSearch = [
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_MISC,
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_PRE_TRAVEL_INFO,
  PAGE_TYPE_PROPERTY,
  PAGE_TYPE_AREA,
];

const prepareSubmitValues = ({ app, ...values }, isSample) => {
  const preparedValues = {
    ...values,

    customMobileAppId: app.id,

    commonPages:
      values.commonPages?.map((item) => ({
        id: item.value,
        ...(item.rooms
          ? { rooms: item.rooms.map((room) => (room.id ? { id: room.id } : omit(['key', 'label', 'value'], room))) }
          : {}),
      })) ?? [],
    whiteLabelingDisplayCompanyLogo: !values?.whiteLabelingOn ? false : values?.whiteLabelingDisplayCompanyLogo,
  };

  if (isSample) {
    return preparedValues;
  }

  return {
    ...preparedValues,
    tourRequest: {
      id: typeof values.tourRequest.id === 'string' ? undefined : values.tourRequest.id,
      fileName: typeof values.tourRequest.id === 'string' ? values.tourRequest.id : undefined,
    },
  };
};

const decoratedAxiosGet = axiosGetAutoCancelDecorator();

function GeneralInfo() {
  useScrollPageToTop();
  const lookbook = useSelector(itinerariesItemDataSelector);
  const { onAfterSave } = useTryToRemindCheckPrimaryImageForGeneralTab({
    itineraryItemName: 'lookbook',
    neededLastAvailableStep: LOOKBOOK_STEPS.LANDING,
  });
  const handleNextStep = useItinerariesItemNextStepFormHandler({ onAfterNext: onAfterSave });
  const { libType } = useParams();
  const isSampleLookbook = isSample(libType);
  const [tourRequestId] = useQueryParam('tourRequestId', StringParam);

  const { validate, error, isLoading } = useGeneralInfo(isSampleLookbook);
  const handleSave = useItinerariesItemSaveHandler({ onAfterSave });

  const [isSaving, setIsSaving] = React.useState(false);
  const initialValues = useMemo(
    () =>
      prepareInitialValues(
        {
          ...lookbook,
          commonPages: lookbook.commonPages?.map((item) => {
            return {
              label: item.handle || item.name,
              value: item.id,
              type: item.type,
              editedInProposal: item.editedInProposal,
              ...(item.rooms ? { rooms: item.rooms } : {}),
            };
          }),
        },
        { tourRequestId },
      ),
    [lookbook, tourRequestId],
  );

  const withContentMissingWarning = (fn) => async (values) => {
    const hasContent = isDefined(values?.aboutSafariPageId) || !isEmpty(values?.commonPages);

    if (!hasContent) {
      try {
        await showModal(area, (proceed, stop) => (
          <LookbookContentMissingWarningModal onSave={proceed} onClose={stop} />
        ));
      } catch {
        return;
      }
    }
    return fn(values);
  };

  if (isLoading) {
    return (
      <Backdrop isOpen>
        <Loader />
      </Backdrop>
    );
  }

  if (error) {
    return <ErrorScreen status="404" />;
  }

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        <Form
          onSubmit={(values) =>
            withContentMissingWarning(handleNextStep)(prepareSubmitValues(values, isSampleLookbook))
          }
          initialValues={initialValues}
          validate={validate}
          destroyOnUnregister
          render={({ handleSubmit, submitting, submitError, hasValidationErrors, values }) => {
            return (
              <form onSubmit={handleSubmit} autoComplete="nope" className={styles.form}>
                <UnsavedChangesFormWatcher />

                {submitting && <Loader isFillBlock />}

                <FormSection type="rounded">
                  <InternalSection />
                </FormSection>

                <FormSection type="rounded">
                  <CoverSection />
                </FormSection>

                <FormSection type="rounded">
                  <IncludeSection />
                </FormSection>

                <FormSection type="rounded">
                  <WhiteLabeling />
                </FormSection>

                <FormSection type="rounded">
                  <CustomAppsBlock />
                </FormSection>

                {submitError && <FormErrorText>{submitError}</FormErrorText>}

                <FooterComponent>
                  <ItinerariesItemNextButton isSaving={isSaving} />
                </FooterComponent>

                <ItinerariesItemActionButtons
                  hasValidationErrors={hasValidationErrors}
                  submitting={submitting}
                  onSave={() => withContentMissingWarning(handleSave)(prepareSubmitValues(values, isSampleLookbook))}
                  onIsSavingChange={setIsSaving}
                />
              </form>
            );
          }}
        />
      )}
    </LayoutWithFooter>
  );
}

export default GeneralInfo;

const InternalSection = () => {
  const accountId = useSelector(accountIdSelector);
  const showPopup = useFormFieldPopup();
  const { libType } = useParams();
  const isSampleLookbook = isSample(libType);

  const loadFilenameOptions = async (query) => {
    const response = await decoratedAxiosGet(`/acc/${accountId}/tour_requests/lite`, { params: { name_q: query } });

    return response.data.data.map((item) => ({
      value: item.id,
      label: item.fileName,
    }));
  };

  return (
    <>
      <Text type="brandon" isStrong weight="500" className={styles.sectionTitle}>
        Internal Information
      </Text>

      {!isSampleLookbook && (
        <Field
          name="tourRequest.id"
          component={FormGroupAdapter}
          customComponent={Select}
          placeholder="File Name*"
          loadOptions={loadFilenameOptions}
          async
          creatable
          isValidNewOption={(input) => !!input}
          allowCreateWhileLoading
          optionToString
          debounced
          debounceTimeout={500}
          editable
          tooltipContent={<GeneralInfoTooltip />}
        />
      )}

      <Field
        component={FormGroupAdapter}
        name="notes"
        placeholder="Internal notes..."
        type="textarea"
        minRows={3}
        className={styles.textArea}
        noMargin
        parse={(value) => (value === '' ? null : value)}
        onFocus={showPopup(
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Tool Tip:</b>
            <br />
            <br />
            Enter some internal notes to remind yourself what is included in this itinerary.
            <br />
            <br />
            This will help you find and recognize it easily later.
          </Text>,
        )}
      />
    </>
  );
};

const CoverSection = () => {
  const showPopup = useFormFieldPopup();
  const lookbook = useSelector(itinerariesItemDataSelector);

  const lookbookForPreview = (() => {
    return {
      ...lookbook,
      title: lookbook.title,
      travelDates: lookbook.travelDates,
      preparedFor: lookbook.preparedFor,
    };
  })();

  return (
    <>
      <div className={styles.coverTitleWrapper}>
        <div>
          <Text type="brandon" isStrong weight="500">
            Cover Page
          </Text>

          <Text type="brandon" size="regular" color="gray-600" className={styles.coverSubTitle}>
            This information will appear on the Cover Page.
          </Text>
        </div>

        <GeneralPopoverPagePreview proposal={lookbookForPreview} />
      </div>

      <Field component={FormGroupAdapter} name="inputGroup" type="inputGroup">
        <Field
          component={FormGroupAdapter}
          name="title"
          placeholder="Proposal Title*"
          type="text"
          autoComplete="off"
          onFocus={showPopup(
            <Text accent style={{ fontSize: '1.5rem' }}>
              <b>Tool Tip:</b>
              <br />
              <br />
              Come up with a catchy title for your itinerary proposal.
              <br />
              Keep it short and snappy.
              <br />
              <br />
              <b>Examples:</b>
              <br />
              <br />
              The Best of East Africa
              <br />
              <br />
              A Tailor Made
              <br />
              Honeymoon Safari
              <br />
              <br />
              Culture & Adventure
            </Text>,
          )}
        />
        <Field
          component={FormGroupAdapter}
          name="preparedFor"
          placeholder="Prepared For"
          type="text"
          parse={(value) => value || null}
          onFocus={showPopup(
            <Text accent style={{ fontSize: '1.5rem' }}>
              <b>Tool Tip:</b>
              <br />
              <br />
              Guests’ names.
              <br />
              <br />
              <b>Examples:</b>
              <br />
              <br />
              Mr & Mrs Smith
              <br />
              <br />
              The Doe Family
              <br />
              <br />
              John Smith & Friends
            </Text>,
          )}
        />
      </Field>

      <Text type="brandon" size="regular" className={styles.coverSectionDatesTitle}>
        Dates of travel or approximate travel window.
      </Text>

      <Field
        component={FormGroupAdapter}
        name="travelDates"
        placeholder="Enter Dates"
        type="text"
        noMargin
        parse={(value) => value || null}
        onFocus={showPopup(
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Examples:</b>
            <br />
            <br />
            September 2027
            <br />
            <br />
            August 21 -
            <br />
            September 20, 2027
            <br />
            <br />
            Christmas 2028
          </Text>,
        )}
      />
    </>
  );
};

const IncludeSection = () => {
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { initialValues, values, dirtyFields } = useFormState();
  const form = useForm();

  return (
    <>
      <div className={styles.includeTitleWrapper}>
        <Text type="brandon" isStrong weight="500">
          Include Information Pages
        </Text>

        <Text type="brandon" size="regular" color="gray-600" className={styles.coverSubTitle}>
          Select pages from your content library
        </Text>
      </div>

      <div className={styles.container}>
        <Text type="brandon" size="regular">
          Introductory Notes
        </Text>
        <Field
          name="filtersForIntroductoryNotes"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForIntroductoryNotes}
              filterName="Content Pages"
              filterKeyName="filtersForIntroductoryNotes"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <Field
        component={FormGroupAdapter}
        customComponent={SelectPage}
        extraCreatePageParams={{ forLookbook: true }}
        extraLoadOptionsParams={{ countless: '1' }}
        isClearable
        name="aboutSafariPageId"
        pageType="about_safari"
        optionToString
        creatable={false}
        placeholder="Select Pages"
        className={styles.includeFormGroup}
        additionalOptions={
          initialValues?.aboutSafariPageId && !dirtyFields.aboutSafariPageId
            ? [
                {
                  handle: initialValues.aboutSafariPageHandle,
                  id: initialValues.aboutSafariPageId,
                  name: initialValues.aboutSafariPageName,
                },
              ]
            : []
        }
        additionalQueryParams={values?.filtersForIntroductoryNotes?.data}
        tooltipContent={
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Tool Tip:</b>
            <br />
            <br />
            Search your Content Library to find a Note About Your Safari page that you have previously written and
            saved, or choose to add a new blank one.
            <br />
            <br />
            If you do not want to include a custom Note About Your Safari page simply leave this field blank.
          </Text>
        }
      />

      <div className={styles.container}>
        <Text type="brandon" size="regular">
          Lookbook Inclusions
        </Text>
        <Field
          name="filtersForLookbookInclusions"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForLookbookInclusions}
              filterName="Content Pages"
              filterKeyName="filtersForLookbookInclusions"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <RoomsWrapper
        value={
          values['commonPages']?.reduce((map, page) => {
            if (page.rooms) {
              map[page.value] = page.rooms;
            }
            return map;
          }, {}) || {}
        }
        onChange={async (id, rooms) => {
          const commonPages = form.getFieldState('commonPages').value;
          if (!commonPages || isEmpty(commonPages)) return;
          const index = commonPages.findIndex((page) => page.value === id);
          const newCommonPages = [...commonPages];
          newCommonPages[index] = {
            ...newCommonPages[index],
            rooms,
          };
          form.change('commonPages', newCommonPages);
        }}
      >
        {(selectProps) => (
          <Field
            component={FormGroupAdapter}
            customComponent={PageMultiselect}
            placeholder="Select Pages"
            name="commonPages"
            defaultOptions={values.commonPages}
            typesToSearch={inclusionsPageTypesToSearch}
            additionalQueryParams={values?.filtersForLookbookInclusions?.data}
            {...selectProps}
            onCustomChange={selectProps.onChange}
            noMargin
          />
        )}
      </RoomsWrapper>
    </>
  );
};
